/**
 * Error Recovery Utilities
 * Provides strategies for recovering from various types of errors
 */

import { logger } from "./logger";

/**
 * Error categories for recovery strategies
 */
export enum ErrorCategory {
  NETWORK = "network",
  AUTH = "auth",
  SERVER = "server",
  VALIDATION = "validation",
  PARSING = "parsing",
  UNKNOWN = "unknown",
}

/**
 * Error recovery strategy
 */
export interface ErrorRecoveryStrategy {
  category: ErrorCategory;
  canRetry: boolean;
  maxRetries: number;
  retryDelay: number; // in milliseconds
  shouldRetry: (error: Error, attemptCount: number) => boolean;
  onRetry?: (error: Error, attemptCount: number) => void;
  onFail?: (error: Error) => void;
}

/**
 * Categorize an error
 */
export function categorizeError(error: Error | unknown): ErrorCategory {
  if (!(error instanceof Error)) {
    return ErrorCategory.UNKNOWN;
  }

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // Network errors
  if (
    name.includes("network") ||
    name.includes("fetch") ||
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("timeout")
  ) {
    return ErrorCategory.NETWORK;
  }

  // Auth errors
  if (
    name.includes("auth") ||
    message.includes("unauthorized") ||
    message.includes("forbidden") ||
    message.includes("token") ||
    message.includes("401") ||
    message.includes("403")
  ) {
    return ErrorCategory.AUTH;
  }

  // Validation errors
  if (
    name.includes("validation") ||
    message.includes("validation") ||
    message.includes("invalid") ||
    message.includes("required")
  ) {
    return ErrorCategory.VALIDATION;
  }

  // Parsing errors
  if (
    name.includes("syntax") ||
    name.includes("parse") ||
    message.includes("parse") ||
    message.includes("json")
  ) {
    return ErrorCategory.PARSING;
  }

  // Server errors
  if (
    message.includes("500") ||
    message.includes("502") ||
    message.includes("503") ||
    message.includes("504") ||
    message.includes("internal server error")
  ) {
    return ErrorCategory.SERVER;
  }

  return ErrorCategory.UNKNOWN;
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: Error | unknown): boolean {
  const category = categorizeError(error);

  switch (category) {
    case ErrorCategory.NETWORK:
    case ErrorCategory.SERVER:
      return true;
    case ErrorCategory.AUTH:
    case ErrorCategory.VALIDATION:
    case ErrorCategory.PARSING:
      return false;
    default:
      return false;
  }
}

/**
 * Get retry delay with exponential backoff
 */
export function getRetryDelay(
  attemptCount: number,
  baseDelay: number = 1000,
  maxDelay: number = 30000
): number {
  const delay = baseDelay * Math.pow(2, attemptCount);
  return Math.min(delay, maxDelay);
}

/**
 * Retry a function with exponential backoff
 * @lintignore - Utility function, kept for future use
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    baseDelay?: number;
    maxDelay?: number;
    onRetry?: (error: Error, attemptCount: number) => void;
    shouldRetry?: (error: Error, attemptCount: number) => boolean;
  } = {}
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 30000,
    onRetry,
    shouldRetry = isRetryableError,
  } = options;

  let lastError: Error | unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      if (attempt < maxRetries && shouldRetry(error as Error, attempt)) {
        const delay = getRetryDelay(attempt, baseDelay, maxDelay);

        if (onRetry) {
          onRetry(error as Error, attempt + 1);
        }

        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Don't retry, throw the error
      throw error;
    }
  }

  throw lastError!;
}

/**
 * Create a recovery strategy for a specific error category
 */
/** @lintignore - Utility function, kept for future use */
export function createRecoveryStrategy(
  category: ErrorCategory
): ErrorRecoveryStrategy {
  const baseStrategies: Record<
    ErrorCategory,
    Omit<ErrorRecoveryStrategy, "category">
  > = {
    [ErrorCategory.NETWORK]: {
      canRetry: true,
      maxRetries: 3,
      retryDelay: 1000,
      shouldRetry: (error: Error, attemptCount: number) =>
        attemptCount < 3 && isRetryableError(error),
    },
    [ErrorCategory.SERVER]: {
      canRetry: true,
      maxRetries: 2,
      retryDelay: 2000,
      shouldRetry: (error: Error, attemptCount: number) =>
        attemptCount < 2 && isRetryableError(error),
    },
    [ErrorCategory.AUTH]: {
      canRetry: false,
      maxRetries: 0,
      retryDelay: 0,
      shouldRetry: () => false,
    },
    [ErrorCategory.VALIDATION]: {
      canRetry: false,
      maxRetries: 0,
      retryDelay: 0,
      shouldRetry: () => false,
    },
    [ErrorCategory.PARSING]: {
      canRetry: false,
      maxRetries: 0,
      retryDelay: 0,
      shouldRetry: () => false,
    },
    [ErrorCategory.UNKNOWN]: {
      canRetry: false,
      maxRetries: 0,
      retryDelay: 0,
      shouldRetry: () => false,
    },
  };

  return {
    category,
    ...baseStrategies[category],
  };
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyErrorMessage(error: Error | unknown): string {
  const category = categorizeError(error);

  if (!(error instanceof Error)) {
    return "An unexpected error occurred";
  }

  switch (category) {
    case ErrorCategory.NETWORK:
      return "Network error. Please check your internet connection and try again.";
    case ErrorCategory.AUTH:
      return "Authentication error. Please log in again.";
    case ErrorCategory.SERVER:
      return "Server error. Please try again in a moment.";
    case ErrorCategory.VALIDATION:
      return "Invalid input. Please check your data and try again.";
    case ErrorCategory.PARSING:
      return "Data format error. Please refresh the page.";
    default:
      return error.message || "An unexpected error occurred";
  }
}

/**
 * Store error in localStorage for later recovery/analysis
 */
export function storeErrorForRecovery(
  error: Error,
  context?: Record<string, unknown>
): void {
  try {
    const errorData = {
      message: error.message,
      stack: error.stack,
      name: error.name,
      timestamp: Date.now(),
      context,
    };

    const stored = localStorage.getItem("error_recovery_queue");
    const errors = stored ? JSON.parse(stored) : [];
    errors.push(errorData);

    // Keep only last 10 errors
    const trimmedErrors = errors.slice(-10);
    localStorage.setItem("error_recovery_queue", JSON.stringify(trimmedErrors));
  } catch (err) {
    // Silently fail - we don't want error recovery to cause more errors
    logger.error(
      "Failed to store error for recovery",
      err instanceof Error ? err : undefined,
      {
        operation: "storeErrorForRecovery",
      }
    );
  }
}

/**
 * Clear stored errors
 */
/** @lintignore - Utility function, kept for future use */
export function clearStoredErrors(): void {
  try {
    localStorage.removeItem("error_recovery_queue");
  } catch (err) {
    logger.error(
      "Failed to clear stored errors",
      err instanceof Error ? err : undefined,
      {
        operation: "clearStoredErrors",
      }
    );
  }
}

/**
 * Get stored errors
 */
/** @lintignore - Utility function, kept for future use */
export function getStoredErrors(): Array<{
  message: string;
  stack?: string;
  name: string;
  timestamp: number;
  context?: Record<string, unknown>;
}> {
  try {
    const stored = localStorage.getItem("error_recovery_queue");
    return stored ? JSON.parse(stored) : [];
  } catch (err) {
    logger.error(
      "Failed to get stored errors",
      err instanceof Error ? err : undefined,
      {
        operation: "getStoredErrors",
      }
    );
    return [];
  }
}
